import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronRight,
  UserPlus,
  LogIn,
  Eye,
  EyeOff,
  Sparkles,
  Globe,
  ShieldCheck,
  Headphones,
  Crown,
} from 'lucide-react';
import { API } from '@/context/AuthContext';
import { waLink, BITZ_WHATSAPP_DISPLAY } from '@/config/contact';
import { playShimmerCue } from '@/lib/bitzSonic';

/**
 * PremiumHero — Cinematic, international-grade hero for BITZ Club International.
 *
 * Layout:
 *   Desktop (lg+) : 2-column → cinematic copy LEFT, member access card RIGHT
 *   Mobile        : stacked → image, copy, CTAs, form
 *
 * Branding rules:
 *   - Luxury black-gold aesthetic
 *   - International-standard typography & spacing
 *   - Positions BITZ as: Premium Lifestyle + Smart Everyday Savings ecosystem
 *   - Preserves WhatsApp CTA via waLink() helper
 */

// Curated international stills representing the *type* of experiences our
// member network unlocks across affiliated partners. None of these visuals
// are BCI-owned — they illustrate the kind of hospitality, lifestyle and
// networking access the membership opens up.
const FALLBACK_STILLS = [
  { src: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1920&q=85&auto=format&fit=crop', label: 'Member Access · Resorts' },
  { src: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1920&q=85&auto=format&fit=crop', label: 'Affiliated Dining' },
  { src: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=1920&q=85&auto=format&fit=crop', label: 'Hospitality Affiliations' },
  { src: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1920&q=85&auto=format&fit=crop', label: 'Wellness Partner Network' },
  { src: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=1920&q=85&auto=format&fit=crop', label: 'Curated Lifestyle Access' },
  { src: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920&q=85&auto=format&fit=crop', label: 'Beachside Partner Stays' },
  { src: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=85&auto=format&fit=crop', label: 'Networking Lounges' },
  { src: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1920&q=85&auto=format&fit=crop', label: 'Workation Experiences' },
];

// Trust badges — positions BCI as an access network, not an owner of venues
const TRUST_BADGES = [
  { icon: Globe, label: '600+ Affiliated Venues', accent: '#D4AF37' },
  { icon: Sparkles, label: 'Daily Needs Ecosystem', accent: '#D4AF37' },
  { icon: Headphones, label: '24/7 Member Concierge', accent: '#D4AF37' },
  { icon: ShieldCheck, label: 'Curated Partner Network', accent: '#D4AF37' },
];

// ---- Inline mini-forms (same backend wiring as before, just restyled) ----

const HeroEnquiryForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', mobile: '', email: '', city: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile) {
      toast.error('Please fill in name and mobile');
      return;
    }
    navigate(
      `/register?name=${encodeURIComponent(formData.name)}&mobile=${formData.mobile}&email=${encodeURIComponent(
        formData.email || ''
      )}&city=${encodeURIComponent(formData.city || '')}`
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="hero-enquiry-form">
      <div>
        <input
          type="text"
          placeholder="Full Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-4 py-3 bg-white/95 border border-white/20 rounded-lg text-gray-900 placeholder-gray-500 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 outline-none text-sm"
          required
          data-testid="hero-name-input"
        />
      </div>
      <div>
        <input
          type="tel"
          placeholder="Mobile Number"
          value={formData.mobile}
          onChange={(e) =>
            setFormData({ ...formData, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })
          }
          className="w-full px-4 py-3 bg-white/95 border border-white/20 rounded-lg text-gray-900 placeholder-gray-500 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 outline-none text-sm"
          required
          data-testid="hero-mobile-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input
          type="email"
          placeholder="Email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full px-4 py-3 bg-white/95 border border-white/20 rounded-lg text-gray-900 placeholder-gray-500 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 outline-none text-sm"
        />
        <input
          type="text"
          placeholder="City"
          value={formData.city}
          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
          className="w-full px-4 py-3 bg-white/95 border border-white/20 rounded-lg text-gray-900 placeholder-gray-500 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 outline-none text-sm"
        />
      </div>
      <button
        type="submit"
        className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-black font-semibold rounded-lg hover:shadow-[0_10px_30px_-10px_rgba(212,175,55,0.6)] transition-all text-sm tracking-wide"
        data-testid="hero-enquiry-submit"
      >
        Continue Registration →
      </button>
      <p className="text-center text-[11px] text-gray-300/80 leading-relaxed">
        By continuing you agree to our membership terms.
      </p>
    </form>
  );
};

const HeroLoginForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ mobile: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.mobile || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        identifier: formData.mobile,
        password: formData.password,
      });
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        toast.success('Login successful');
        const role = response.data.user?.role;
        if (role === 'admin' || role === 'super_admin') navigate('/admin');
        else if (role === 'telecaller') navigate('/telecaller');
        else navigate('/member');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="hero-login-form">
      <input
        type="tel"
        placeholder="Mobile Number"
        value={formData.mobile}
        onChange={(e) =>
          setFormData({ ...formData, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })
        }
        className="w-full px-4 py-3 bg-white/95 border border-white/20 rounded-lg text-gray-900 placeholder-gray-500 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 outline-none text-sm"
        required
        data-testid="hero-login-mobile"
      />
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          placeholder="Password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          className="w-full px-4 py-3 bg-white/95 border border-white/20 rounded-lg text-gray-900 placeholder-gray-500 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 outline-none text-sm pr-11"
          required
          data-testid="hero-login-password"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          tabIndex={-1}
        >
          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-black font-semibold rounded-lg hover:shadow-[0_10px_30px_-10px_rgba(212,175,55,0.6)] transition-all text-sm tracking-wide disabled:opacity-50"
        data-testid="hero-login-submit"
      >
        {loading ? 'Signing in…' : 'Access Member Portal →'}
      </button>
      <p className="text-center text-[11px] text-gray-300/80">
        Forgot password?{' '}
        <Link to="/contact" className="text-[#D4AF37] hover:underline" data-testid="forgot-password-contact-link">
          Contact us
        </Link>
      </p>
    </form>
  );
};

// ---- Main hero ----

const PremiumHero = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [accessTab, setAccessTab] = useState('register');
  const reduceMotion = useReducedMotion();

  // Detect small viewports — disable Ken Burns scale animation on mobile
  // for perfectly smooth performance on lower-end devices.
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsSmallScreen(e.matches);
    setIsSmallScreen(mq.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);

  const useKenBurns = !reduceMotion && !isSmallScreen;

  // Use curated, international-grade luxury stills for the hero — guarantees
  // consistent premium quality and avoids inconsistent partner photo sizes.
  const slides = FALLBACK_STILLS;

  // Auto-rotate slides every 8s — calm, lounge-grade pacing.
  useEffect(() => {
    if (slides.length === 0) return;
    const id = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 8000);
    return () => clearInterval(id);
  }, [slides.length]);

  // Harmonic shimmer on every slide transition (skipped on first paint)
  const firstSlideRef = useRef(true);
  useEffect(() => {
    if (firstSlideRef.current) {
      firstSlideRef.current = false;
      return;
    }
    // Brother feedback: the short shimmer "tinkle" on slide change felt disruptive.
    // We rely entirely on the continuous ambient pad (lobby-style background music)
    // launched from <AmbientControl />, so the per-slide cue is intentionally muted.
    // playShimmerCue();
  }, [activeSlide]);

  const scrollToPlans = () => {
    const el = document.getElementById('plans');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const inviteMessage =
    "Hello BITZ Club International — I'd like to request an invite for membership. Please share details.";

  return (
    <section
      className="relative w-full overflow-hidden pt-24 md:pt-28 bg-[#0A0A0B]"
      data-testid="premium-hero"
      aria-label="BITZ Club International — premium membership hero"
    >
      {/* ───────────────────────────────────────────────
          Layer 1 — Cinematic image carousel (Ken Burns)
         ─────────────────────────────────────────────── */}
      <div className="absolute inset-0 z-0">
        <AnimatePresence mode="sync">
          {slides.map((slide, i) =>
            i === activeSlide ? (
              <motion.div
                key={slide.src}
                initial={{ opacity: 0, scale: useKenBurns ? 1.06 : 1 }}
                animate={{ opacity: 1, scale: useKenBurns ? 1.0 : 1 }}
                exit={{ opacity: 0, scale: useKenBurns ? 1.0 : 1 }}
                transition={{
                  opacity: { duration: 2.2, ease: [0.22, 1, 0.36, 1] },
                  scale: useKenBurns
                    ? { duration: 9, ease: 'linear' }
                    : { duration: 0 },
                }}
                className="absolute inset-0"
                style={{ willChange: 'opacity' }}
              >
                <img
                  src={slide.src}
                  alt={slide.label || 'BITZ Club partner venue'}
                  className="absolute inset-0 w-full h-full object-cover object-center"
                  loading={i === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </motion.div>
            ) : null
          )}
        </AnimatePresence>

        {/* Glass shimmer — a single diagonal light sweep on every slide change.
            Re-mounted via key={activeSlide}; never loops, never tiles. */}
        {!reduceMotion && (
          <motion.div
            key={`sweep-${activeSlide}`}
            initial={{ x: '-110%', opacity: 0 }}
            animate={{ x: '120%', opacity: [0, 0.55, 0] }}
            transition={{ duration: 2.0, ease: [0.4, 0, 0.2, 1] }}
            className="absolute inset-y-0 -inset-x-1/4 pointer-events-none mix-blend-overlay"
            style={{
              background:
                'linear-gradient(105deg, transparent 42%, rgba(255,255,255,0.09) 50%, transparent 58%)',
              willChange: 'transform, opacity',
            }}
            aria-hidden
          />
        )}

        {/* Cinematic overlays — black-to-transparent for text legibility, gold vignette for warmth */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-black/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/20" />
        <div
          className="absolute inset-0 opacity-30 mix-blend-soft-light pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 75% 35%, rgba(212,175,55,0.55), transparent 55%)',
          }}
        />
        {/* Subtle film grain via SVG noise — adds editorial texture */}
        <div
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
      </div>

      {/* ───────────────────────────────────────────────
          Layer 2 — Content grid
         ─────────────────────────────────────────────── */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* LEFT — Cinematic copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 text-left"
          >
            {/* Eyebrow tagline */}
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span
                className="text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#D4AF37] font-medium"
                style={{ fontFamily: 'Cinzel, Playfair Display, serif' }}
              >
                BITZ Club International · BCI
              </span>
            </div>

            {/* Main headline */}
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-6"
              style={{ fontFamily: 'Playfair Display, Cinzel, serif' }}
              data-testid="hero-headline"
            >
              Live Premium.
              <br />
              Spend Smarter.
              <br />
              <span className="bg-gradient-to-r from-[#F5D77A] via-[#D4AF37] to-[#A67C00] bg-clip-text text-transparent">
                Belong to BITZ.
              </span>
            </h1>

            {/* Sub-headline — positions BCI as a member-access network of partners */}
            <p
              className="text-base sm:text-lg lg:text-xl text-gray-200/90 leading-relaxed max-w-2xl mb-8"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              A members-only lifestyle network giving you access to curated hotels,
              resorts, dining, wellness and workation partners — plus a trusted
              daily-needs ecosystem. One card. A growing world of affiliations.
            </p>

            {/* Dual CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-10">
              <a
                href={waLink(inviteMessage)}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-black font-semibold rounded-xl hover:shadow-[0_18px_50px_-12px_rgba(212,175,55,0.7)] hover:translate-y-[-2px] transition-all text-sm sm:text-base tracking-wide"
                data-testid="hero-request-invite-btn"
              >
                <Crown className="w-5 h-5" />
                Request an Invite
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <button
                type="button"
                onClick={scrollToPlans}
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/5 backdrop-blur-md text-white font-medium rounded-xl border border-white/20 hover:bg-white/10 hover:border-[#D4AF37]/50 transition-all text-sm sm:text-base tracking-wide"
                data-testid="hero-explore-club-btn"
              >
                Explore the Club
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Trust strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-2xl">
              {TRUST_BADGES.map((b) => {
                const Ico = b.icon;
                return (
                  <div
                    key={b.label}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-white/[0.04] backdrop-blur-sm border border-white/10 hover:border-[#D4AF37]/30 transition-colors"
                    data-testid={`hero-trust-${b.label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                  >
                    <Ico className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-[11px] sm:text-xs text-gray-200/90 font-medium leading-tight">
                      {b.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Bottom hint — phone */}
            <p className="mt-8 text-[11px] sm:text-xs text-gray-400 tracking-wide">
              For corporate and special enquiries · {BITZ_WHATSAPP_DISPLAY}
            </p>
          </motion.div>

          {/* RIGHT — Member access card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              {/* Gold glow halo */}
              <div className="absolute -inset-1 bg-gradient-to-br from-[#D4AF37]/30 via-transparent to-[#D4AF37]/10 rounded-3xl blur-xl opacity-60 pointer-events-none" />

              <div className="relative bg-gradient-to-br from-[#101012]/95 to-[#1A1A1C]/95 backdrop-blur-xl border border-[#D4AF37]/20 rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
                {/* Card header */}
                <div className="px-6 sm:px-7 pt-6 pb-4 border-b border-white/5">
                  <p
                    className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-1"
                    style={{ fontFamily: 'Cinzel, serif' }}
                  >
                    Members & Guests
                  </p>
                  <h3
                    className="text-xl sm:text-2xl font-bold text-white"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {accessTab === 'register' ? 'Begin Your Membership' : 'Welcome Back'}
                  </h3>
                </div>

                {/* Tab switcher */}
                <div className="flex border-b border-white/5">
                  <button
                    onClick={() => setAccessTab('register')}
                    className={`flex-1 py-3 px-4 text-xs sm:text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                      accessTab === 'register'
                        ? 'text-[#D4AF37] bg-[#D4AF37]/5 border-b-2 border-[#D4AF37]'
                        : 'text-gray-400 hover:text-white border-b-2 border-transparent'
                    }`}
                    data-testid="hero-tab-register"
                  >
                    <UserPlus className="w-4 h-4" />
                    Register
                  </button>
                  <button
                    onClick={() => setAccessTab('login')}
                    className={`flex-1 py-3 px-4 text-xs sm:text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                      accessTab === 'login'
                        ? 'text-[#D4AF37] bg-[#D4AF37]/5 border-b-2 border-[#D4AF37]'
                        : 'text-gray-400 hover:text-white border-b-2 border-transparent'
                    }`}
                    data-testid="hero-tab-login"
                  >
                    <LogIn className="w-4 h-4" />
                    Member Login
                  </button>
                </div>

                {/* Form body */}
                <div className="p-6 sm:p-7 bg-gradient-to-b from-transparent to-black/30">
                  {accessTab === 'register' ? <HeroEnquiryForm /> : <HeroLoginForm />}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ───────────────────────────────────────────────
            Slide indicators + caption
           ─────────────────────────────────────────────── */}
        <div className="mt-12 lg:mt-16 flex items-center justify-between gap-6">
          {/* Caption */}
          <div className="flex items-center gap-3 text-gray-400">
            <span className="h-px w-8 bg-[#D4AF37]/40" />
            <span
              className="text-[10px] sm:text-xs tracking-[0.3em] uppercase"
              style={{ fontFamily: 'Cinzel, serif' }}
            >
              {slides[activeSlide]?.label || ''}
            </span>
          </div>

          {/* Dots */}
          <div className="flex items-center gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveSlide(i)}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`hero-slide-dot-${i}`}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === activeSlide
                    ? 'w-10 bg-[#D4AF37]'
                    : 'w-4 bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom fade-out → blends into next section */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-b from-transparent to-[#0F0F10] pointer-events-none z-10" />
    </section>
  );
};

export default PremiumHero;
